import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { Session } from '../../api/session';
import { getObject, getObjectAddresses, getPassesPage, type ObjectInfo } from '../../api/tenant';
import { GuestType } from '../../domain/passes';
import { PASS_TABS, type PassTabId } from '../../domain/passTabs';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import { PassRow } from '../common/PassRow';
import s from './passes.module.css';

const EMPTY: Record<PassTabId, string> = {
  active: 'Активных пропусков нет',
  waiting: 'Нет пропусков, ожидающих гостя или согласования',
  archive: 'Архив пуст',
};

function Toggle({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" aria-pressed={on} className={on ? `${s.filter} ${s.filterOn}` : s.filter} onClick={onClick}>
      {label}
    </button>
  );
}

/** «Пропуска»: tabs by status, search, filter chips, 20 per page (spec §6.5). */
export function PassesScreen({ session, object }: { session: Session; object: ObjectInfo }) {
  const [tab, setTab] = useState<PassTabId>('active');
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [addressId, setAddressId] = useState<number | null>(null);
  const [guestType, setGuestType] = useState<number | null>(null);
  const [fromConfidant, setFromConfidant] = useState(false);

  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const statuses = PASS_TABS.find((t) => t.id === tab)!.statuses;

  const passes = useInfiniteQuery({
    queryKey: ['passes', 'list', tab, search, addressId, guestType, fromConfidant],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getPassesPage(session, {
        statuses: [...statuses],
        query: search,
        addressId: addressId ?? undefined,
        guestType: guestType ?? undefined,
        fromConfidant,
        page: pageParam,
      }),
    getNextPageParam: (last) => last.nextPage ?? undefined,
  });

  // Without an address filter the list shows only this object's addresses (the API lists all of the resident's).
  const ids = new Set((addresses.data ?? []).map((a) => a.id));
  const items = (passes.data?.pages ?? []).flatMap((p) => p.items).filter((p) => !addresses.data || ids.has(p.address.id));
  const confidences = details.data?.settings.confidencesEnable === true;

  return (
    <div className={s.page}>
      <h1 className={s.title}>Пропуска</h1>

      <form
        role="search"
        className={s.search}
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(draft);
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="6" />
          <path d="M20 20l-4.5-4.5" />
        </svg>
        <input
          type="search"
          aria-label="Поиск по номеру или имени"
          placeholder="Номер или имя"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (!e.target.value) setSearch('');
          }}
          enterKeyHint="search"
        />
      </form>

      <div role="tablist" aria-label="Статус пропусков" className={s.tabs}>
        {PASS_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={t.id === tab} className={t.id === tab ? `${s.tab} ${s.tabOn}` : s.tab} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={s.filters} aria-label="Фильтры">
        {(addresses.data?.length ?? 0) > 1 &&
          addresses.data!.map((a) => (
            <Toggle key={a.id} on={addressId === a.id} label={a.name} onClick={() => setAddressId(addressId === a.id ? null : a.id)} />
          ))}
        <Toggle on={guestType === GuestType.Vehicle} label="На машине" onClick={() => setGuestType(guestType === GuestType.Vehicle ? null : GuestType.Vehicle)} />
        <Toggle on={guestType === GuestType.Pedestrian} label="Пешком" onClick={() => setGuestType(guestType === GuestType.Pedestrian ? null : GuestType.Pedestrian)} />
        {confidences && <Toggle on={fromConfidant} label="Доверенное лицо" onClick={() => setFromConfidant((v) => !v)} />}
      </div>

      {passes.isPending ? (
        <Skeleton height={200} />
      ) : passes.isError ? (
        <LoadFailed error={passes.error} onRetry={() => void passes.refetch()} />
      ) : items.length === 0 ? (
        <p className={s.empty}>{search ? 'Ничего не нашлось' : EMPTY[tab]}</p>
      ) : (
        <ul className={s.list} aria-label="Пропуска">
          {items.map((p) => (
            <li key={p.id}>
              <PassRow pass={p} withType />
            </li>
          ))}
        </ul>
      )}

      {passes.hasNextPage && (
        <button type="button" className={s.more} onClick={() => void passes.fetchNextPage()} disabled={passes.isFetchingNextPage}>
          {passes.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
        </button>
      )}
    </div>
  );
}
